import CloseIcon from 'assets/ver2/icons/close-circle-rounded-icon.svg';
import SizeBox from 'components/common/SizeBox';
import {
  DESKTOP_MIN_WIDTH,
  FUNC_DEFAULT,
  MOBILE_MIN_WIDTH,
  OBJECT_DEFAULT,
  TABLET_MAX_WIDTH,
} from 'constants';
import CustomResendViaDropdown from 'features/to/components/payment/payto/CustomResendViaDropdown';
import mask from 'helpers/mask';
import useCommonStore from 'hooks/useCommonStore';
import useCountdownToActionVer2 from 'hooks/useCountdownToActionVer2';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import {
  cursorPointerMixin,
  displayFlexColCenterCenterMixin,
  displayFlexRowCenterCenterMixin,
  fontSize14Mixin,
  paragraph500Mixin,
  paragraph600Mixin,
} from 'styles';
import { numberUtils } from 'utils';

const PaytoOTPVerificationPopup = ({
  isOpen,
  onClick: handleOnPay,
  onClose,
  isBackdropOverlap,
  zIndexBackdropOverlap,
  zIndexModal = 0,
  onPaytoPaymentProcessing,
  onCheckReceiverVerifiedByOTP,
  onOpenPaytoErrorCodeModal,
  modalData,
  modalOptions,
}) => {
  const { t } = useLang();
  const { currentUser, currentReceiver, setLoading, setPending } =
    useCommonStore();

  const { transactionPayload } = modalData || {};
  const [transactionPayloadLatest, setTransactionPayloadLatest] =
    useState(OBJECT_DEFAULT);

  const { mobile, email } = currentUser || {};

  const codeRef1 = useRef('');
  const codeRef2 = useRef('');
  const codeRef3 = useRef('');
  const codeRef4 = useRef('');
  const codeRef5 = useRef('');
  const codeRef6 = useRef('');

  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [code3, setCode3] = useState('');
  const [code4, setCode4] = useState('');
  const [code5, setCode5] = useState('');
  const [code6, setCode6] = useState('');

  const [codes, setCodes] = useState([
    { ref: codeRef1, code: code1, setCode: setCode1 },
    { ref: codeRef2, code: code2, setCode: setCode2 },
    { ref: codeRef3, code: code3, setCode: setCode3 },
    { ref: codeRef4, code: code4, setCode: setCode4 },
    { ref: codeRef5, code: code5, setCode: setCode5 },
    { ref: codeRef6, code: code6, setCode: setCode6 },
  ]);

  const [isResendViaEmail, setResendViaEmail] = useState(false);
  const [isResending, setResending] = useState(false);

  useEffect(() => {
    setLoading(false);
    codeRef1.current.focus();
  }, []);

  useEffect(() => {
    setTransactionPayloadLatest(transactionPayload || {});
  }, [transactionPayload]);

  const resetCode = () => {
    setCode1('');
    setCode2('');
    setCode3('');
    setCode4('');
    setCode5('');
    setCode6('');
  };

  const refreshCodes = () => {
    setCodes([
      { ref: codeRef1, code: code1, setCode: setCode1 },
      { ref: codeRef2, code: code2, setCode: setCode2 },
      { ref: codeRef3, code: code3, setCode: setCode3 },
      { ref: codeRef4, code: code4, setCode: setCode4 },
      { ref: codeRef5, code: code5, setCode: setCode5 },
      { ref: codeRef6, code: code6, setCode: setCode6 },
    ]);
  };
  useEffect(() => {
    refreshCodes();
  }, [code1, code2, code3, code4, code5, code6]);

  const handleResendTimeout = () => {
    setResending(false);
  };
  const { countdown } = useCountdownToActionVer2({
    countdownInit: 60,
    isRun: isResending,
    cb: handleResendTimeout,
    deps: [isResendViaEmail, isResending],
  });

  const getOTPCode = (endCode) => {
    if (!endCode) return code1 + code2 + code3 + code4 + code5 + code6;
    return code1 + code2 + code3 + code4 + code5 + endCode;
  };

  const handleOnChange = (e, item, itemNext) => {
    let { value } = e.target || {};

    if (!numberUtils.isNumber(value)) return;

    if (value.length > 1) {
      value = value.slice(-1);
    }

    const { ref, setCode } = item;
    const { code: codeNext, ref: refNext } = itemNext || {};

    setCode(value);

    const element = ref?.current;
    const elementNext = refNext?.current;
    if (isEmpty(elementNext)) {
      element?.blur();
      const newTransactionPayload = {
        ...transactionPayloadLatest,
        otp: getOTPCode(value),
      };
      onPaytoPaymentProcessing(
        newTransactionPayload,
        handleOnPay,
        () => {
          setPending(false);
          onClose();
        },
        (errorData) => {
          setPending(false);
          resetCode();
          onOpenPaytoErrorCodeModal(
            errorData,
            '',
            '',
            FUNC_DEFAULT,
            t('button_retry')
          );
        }
      );
    } else {
      if (!codeNext) {
        elementNext?.focus();
      }
    }
  };

  const handleOnFocus = (e, item) => {
    const { setCode } = item;
    setCode('');
  };

  const handleResendCTAOnClick = (isResendViaEmail = false) => {
    setLoading(true);
    resetCode();
    setResendViaEmail(isResendViaEmail);
    onCheckReceiverVerifiedByOTP(
      currentReceiver?.receiverId,
      (otpData) => {
        const newTransactionPayloadLatest = {
          ...transactionPayloadLatest,
          otpToken: otpData?.token,
        };
        setTransactionPayloadLatest(newTransactionPayloadLatest);
        setLoading(false);
        setResending(true);
      },
      (errorData) => {
        const newTransactionPayloadLatest = {
          ...transactionPayloadLatest,
          otpToken: '',
        };
        setTransactionPayloadLatest(newTransactionPayloadLatest);
        setLoading(false);
        onOpenPaytoErrorCodeModal(errorData);
        setResending(false);
      },
      isResendViaEmail
    );
  };

  const getPaytoOtpDesc = () => {
    if (isResendViaEmail)
      return t('popup_payto_otp_desc_2', {
        email: mask.email(email),
      });
    return t('popup_payto_otp_desc_1', {
      phone: mask.phone(mobile),
    });
  };

  const renderCTASection = () => {
    if (isResending)
      return (
        <CTAText
          dangerouslySetInnerHTML={{
            __html: t('popup_payto_otp_cta_2', {
              countdown,
            }),
          }}
        />
      );
    return (
      <CTASection>
        <CTAText>{t('popup_payto_otp_cta')}</CTAText>
        <CustomResendViaDropdown
          text={t('label_resend_via')}
          textStyles={{
            marginRight: '24px',
          }}
          styleDropdown={{
            minHeight: '20px',
            maxHeight: '20px',
          }}
          styleDropdownToggle={{
            paddingInline: '10px',
            paddingRight: '26px',
          }}
          styleDropdownMenu={{
            minWidth: '150px',
            width: '100%',
            transform: 'translate(0px, 20px)',
          }}
          onResendViaEmail={() => handleResendCTAOnClick(true)}
          onResendViaMobile={() => handleResendCTAOnClick()}
        />
      </CTASection>
    );
  };

  return (
    <>
      <style>
        {zIndexModal
          ? `
            @media screen and (max-width: ${TABLET_MAX_WIDTH - 1}px) {
              .modal {
                z-index: ${zIndexModal || 1101};
                background: #fff;
              }
            }
          `
          : ``}
        {isBackdropOverlap
          ? `
            .otp-modal-backdrop {
              z-index: ${zIndexBackdropOverlap || 1100};
              opacity: 1!important;
              display: initial!important;
              background: #fff;
              height: calc(100vh - 70px);
              margin-top: 70px;
            }
          `
          : `
            .otp-modal-backdrop {
              opacity: 1!important;
              display: initial!important;
              background: #fff;
              height: calc(100vh - 70px);
              margin-top: 70px;
            }
          `}
      </style>
      <ModalStyled
        show={isOpen}
        onHide={FUNC_DEFAULT}
        aria-labelledby="contained-modal-title-vcenter"
        backdropClassName="otp-modal-backdrop"
        centered
      >
        <ModalBody>
          <FormOTPVerification>
            <Title>{t('popup_payto_otp_title')}</Title>
            <SizeBox height={10} />
            <Desc dangerouslySetInnerHTML={{ __html: getPaytoOtpDesc() }} />
            <SizeBox height={50} />
            <OTPCodeSection>
              {codes.map((item, index) => (
                <OTPCodeInput
                  key={index}
                  ref={item?.ref}
                  type="tel"
                  value={item?.code}
                  placeholder=""
                  inputMode="decimal"
                  autoComplete="off"
                  $isActive={item?.code}
                  onChange={(e) =>
                    handleOnChange(
                      e,
                      item,
                      index === codes.length - 1 ? null : codes[index + 1]
                    )
                  }
                  onFocus={(e) => handleOnFocus(e, item)}
                />
              ))}
            </OTPCodeSection>
            {/* <button onClick={() => setPending(true)}>click to pending</button> */}
            <SizeBox height={50} />
            {renderCTASection()}
            <SizeBox height={50} />
            <CloseButton
              src={CloseIcon}
              width={50}
              height={50}
              onClick={onClose}
            />
          </FormOTPVerification>
        </ModalBody>
      </ModalStyled>
    </>
  );
};

const ModalStyled = styled(Modal)`
  & .modal-dialog {
    transform: none;
    & .modal-content {
      border-radius: 0;
      box-shadow: none);
      background: var(--ds-c-white);
      border: none;
      width: 420px;
      margin-inline: auto;
      margin-top: 70px;
    }
  }

  @media screen and (min-width: ${MOBILE_MIN_WIDTH}px) and (max-width: ${
  TABLET_MAX_WIDTH - 1
}px) {
    padding: 16px;

    & .modal-dialog {
      margin: 0;
      margin-inline: auto;
      & .modal-content {
        width: 100%;
      }
    }
  }
`;
const ModalBody = styled(Modal.Body)`
  padding: 0;
  margin-bottom: 0px;
  background-color: #fff;
`;

const FormOTPVerification = styled.div`
  ${displayFlexColCenterCenterMixin};
`;
const Title = styled.p`
  ${paragraph600Mixin};
  font-size: 24px;
  line-height: 100%;
  text-align: center;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 40px;
  }
`;
const Desc = styled.p`
  ${paragraph500Mixin};
  text-align: center;
  white-space: break-spaces;
`;
const OTPCodeSection = styled.div`
  ${displayFlexRowCenterCenterMixin};
  gap: 12px;
`;
const OTPCodeInput = styled(Form.Control)`
  ${paragraph600Mixin};
  font-size: 32px;
  line-height: 100%;
  text-align: center;
  width: 44px;
  height: 44px;
  min-height: 44px;
  border: ${(props) =>
    props.$isActive
      ? '1px solid var(--c-primary)'
      : '1px solid var(--ds-c-grey-light)'};
  border-radius: 8px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 60px;
    height: 60px;
    min-height: 60px;
  }

  &:focus {
    border: 1px solid var(--c-primary) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &[readonly] {
    background: #fff;
    border: none;
    color: var(--c-primary);
    cursor: not-allowed;
  }

  &::placeholder {
    ${paragraph600Mixin};
    font-size: 32px;
    line-height: 100%;
    text-transform: none;
  }
`;
const CTAText = styled.p`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  text-align: left;

  & span.resend-cta {
    color: #002ff1;
    text-decoration: underline;
    ${cursorPointerMixin};
  }
`;
const CloseButton = styled.img`
  ${cursorPointerMixin}
`;

const CTASection = styled.div`
  ${displayFlexColCenterCenterMixin};
  gap: 8px;
`;

export default PaytoOTPVerificationPopup;
